import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { Star, ExternalLink, Trophy, Award, ChevronRight, BarChart3, Percent, Zap, Shield, TrendingUp } from 'lucide-react';
import { trackBookmakerClick } from '@/api';
import { trackBookmakerClick as trackGTMClick } from '@/lib/analytics';

const API_BASE = '';

const OUR_RATINGS = {
  '1xbet': 4.3, 'bet365': 4.1, 'pinnacle': 4.0, 'betfair': 4.0,
  'betway': 3.9, 'unibet': 3.9, 'william-hill': 3.8, 'bwin': 3.7,
  'betsson': 3.7, 'paddy-power': 3.7, 'ladbrokes': 3.6, 'betvictor': 3.6,
  'betano': 3.6, 'marathonbet': 3.5, '888sport': 3.5, 'betfred': 3.4,
  'dafabet': 3.4, 'tipico': 3.4, 'sportingbet': 3.3, 'superbet': 3.3,
  'netbet': 3.2, 'comeon': 3.2, 'nordicbet': 3.1, 'fonbet': 3.1,
  'bovada': 3.1, 'bet-10': 3.1, 'expekt': 3.0, 'interwetten': 3.0,
  'betcris': 3.0, '188bet': 2.9,
};

const BRAND = {
  '1xbet':        { accent: '#1a6cbc', textColor: '#fff', logoText: '1XBET' },
  'bet365':       { accent: '#027B5B', textColor: '#ffd700', logoText: 'bet365' },
  'pinnacle':     { accent: '#1B365D', textColor: '#e8c547', logoText: 'PINNACLE' },
  'betfair':      { accent: '#1e1e2f', textColor: '#FFB80C', logoText: 'betfair' },
  'betway':       { accent: '#00a5b5', textColor: '#fff', logoText: 'betway' },
  'unibet':       { accent: '#146B2B', textColor: '#a3e635', logoText: 'unibet' },
  'william-hill': { accent: '#002D5B', textColor: '#fff', logoText: 'W.Hill' },
  'bwin':         { accent: '#1a1a1a', textColor: '#FFD700', logoText: 'bwin' },
  'betsson':      { accent: '#1a1a2e', textColor: '#FF6600', logoText: 'betsson' },
  'paddy-power':  { accent: '#00A651', textColor: '#fff', logoText: 'PP' },
  'ladbrokes':    { accent: '#E0001A', textColor: '#fff', logoText: 'Lads' },
  'betvictor':    { accent: '#1a1a1a', textColor: '#cc0033', logoText: 'BV' },
  'betano':       { accent: '#1a3a2e', textColor: '#2ecc71', logoText: 'betano' },
  'marathonbet':  { accent: '#1a1a2e', textColor: '#4488ee', logoText: 'MBet' },
  '888sport':     { accent: '#1a2e3a', textColor: '#00d4c8', logoText: '888' },
  'betfred':      { accent: '#003DA5', textColor: '#fff', logoText: 'BetFred' },
  'dafabet':      { accent: '#8B0000', textColor: '#ffd700', logoText: 'dafa' },
  'tipico':       { accent: '#003366', textColor: '#fff', logoText: 'tipico' },
  'sportingbet':  { accent: '#1a1a2e', textColor: '#ff3355', logoText: 'SBet' },
  'superbet':     { accent: '#8B0000', textColor: '#ffd700', logoText: 'Super' },
  'netbet':       { accent: '#1a3a1a', textColor: '#66cc66', logoText: 'NetBet' },
  'comeon':       { accent: '#1a2e3a', textColor: '#66bbff', logoText: 'ComeOn' },
  'nordicbet':    { accent: '#1a1a2e', textColor: '#5599cc', logoText: 'Nordic' },
  'expekt':       { accent: '#1a2e1a', textColor: '#66cc66', logoText: 'Expekt' },
  'fonbet':       { accent: '#1a1a2e', textColor: '#FF6600', logoText: 'Fonbet' },
  'interwetten':  { accent: '#1a1a2e', textColor: '#ffcc00', logoText: 'IW' },
  '188bet':       { accent: '#1a1a2e', textColor: '#ff9933', logoText: '188' },
  'betcris':      { accent: '#003366', textColor: '#66aadd', logoText: 'BCris' },
  'bovada':       { accent: '#8B0000', textColor: '#ff6644', logoText: 'BOV' },
  'bet-10':       { accent: '#1a2e3a', textColor: '#33ccff', logoText: '10Bet' },
};

function BrandLogo({ slug, name, size = 'md' }) {
  const brand = BRAND[slug];
  const sizes = { xl: 'w-16 h-16 text-sm', lg: 'w-12 h-12 text-[11px]', md: 'w-10 h-10 text-[9px]', sm: 'w-7 h-7 text-[7px]' };
  const s = sizes[size] || sizes.md;
  if (!brand) return <div className={`${s} rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center`}><span className="text-emerald-400 font-bold">{name?.charAt(0)}</span></div>;
  return (
    <div className={`${s} rounded-xl flex items-center justify-center shadow-lg`} style={{ background: brand.accent, boxShadow: `0 4px 20px ${brand.accent}40` }}>
      <span className="font-black leading-none" style={{ color: brand.textColor, fontFamily: 'var(--font-heading)' }}>{brand.logoText}</span>
    </div>
  );
}

function MiniStars({ rating, size = 'sm' }) {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.3;
  const cls = size === 'lg' ? 'w-4 h-4' : 'w-3 h-3';
  return (
    <div className="flex items-center gap-px">
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} className={`${cls} ${i < full ? 'text-amber-400 fill-amber-400' : i === full && hasHalf ? 'text-amber-400 fill-amber-400/50' : 'text-slate-700'}`} />
      ))}
    </div>
  );
}

function ScoreBar({ rating, color, height = 'h-1.5' }) {
  const pct = (rating / 5) * 100;
  return (
    <div className={`flex-1 ${height} bg-slate-800/80 rounded-full overflow-hidden`}>
      <div className={`${height} rounded-full transition-all duration-700`} style={{ width: `${pct}%`, background: color || '#22c55e' }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   #1 GOLD CARD — Biggest, most prominent
   ═══════════════════════════════════════════════ */
function GoldCard({ b, lang, bp, onVisit, isTR }) {
  const rating = OUR_RATINGS[b.slug] || 0;
  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-amber-500/50 p-[1px]" style={{ background: 'linear-gradient(135deg, #b8860b 0%, #ffd700 25%, #b8860b 50%, #ffd700 75%, #b8860b 100%)' }} data-testid={`bookmaker-card-${b.id}`}>
      <div className="relative rounded-2xl bg-slate-950 p-5 md:p-6">
        {/* Gold glow */}
        <div className="absolute top-0 left-0 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl" />

        <div className="relative z-10 flex items-center gap-5">
          {/* Rank */}
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-black shrink-0 shadow-xl" style={{ background: 'linear-gradient(135deg, #ffd700, #b8860b)', color: '#1a1a00', fontFamily: 'Outfit, sans-serif' }}>
            1
          </div>

          {/* Logo */}
          <Link to={`/${lang}/${bp}/${b.slug}`} className="shrink-0">
            <BrandLogo slug={b.slug} name={b.name} size="xl" />
          </Link>

          {/* Name + Badge */}
          <Link to={`/${lang}/${bp}/${b.slug}`} className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1.5">
              <h2 className="text-xl md:text-2xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.name}</h2>
              <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-500/15 border border-amber-500/40 rounded-full text-[10px] font-bold text-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.15)]">
                <Award className="w-3.5 h-3.5" />{isTR ? 'Editörün Tercihi' : "Editor's Pick"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <MiniStars rating={rating} size="lg" />
              <span className="text-base font-bold text-amber-400 tabular-nums">{rating}</span>
            </div>
          </Link>

          {/* Metrics */}
          <div className="hidden lg:grid grid-cols-4 gap-2">
            {[
              { icon: BarChart3, val: `${rating}/5`, label: isTR ? 'Puan' : 'Score' },
              { icon: Percent, val: `${b.margin_avg || 4}%`, label: isTR ? 'Marj' : 'Margin' },
              { icon: Shield, val: '45+', label: isTR ? 'Pazar' : 'Markets' },
              { icon: Zap, val: '5dk', label: isTR ? 'Hız' : 'Speed' },
            ].map(m => (
              <div key={m.label} className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 text-center min-w-[68px]">
                <m.icon className="w-3.5 h-3.5 mx-auto text-amber-400/70 mb-0.5" />
                <p className="text-sm font-bold text-amber-300 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{m.val}</p>
                <p className="text-[8px] text-amber-400/50 uppercase tracking-wider">{m.label}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <button onClick={(e) => onVisit(b, e)} data-testid={`visit-btn-${b.id}`}
            className="shrink-0 px-6 py-3 rounded-xl text-sm font-bold flex items-center gap-2 transition-all hover:scale-105 shadow-xl"
            style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', boxShadow: '0 4px 24px rgba(34,197,94,0.3)' }}>
            <ExternalLink className="w-4 h-4" />{isTR ? 'Siteye Git' : 'Visit Site'}
          </button>
        </div>

        {/* Score bar */}
        <div className="relative z-10 mt-4 flex items-center gap-3">
          <ScoreBar rating={rating} color="#ffd700" height="h-2" />
          <span className="text-xs text-amber-400/60 tabular-nums">{rating}/5</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   #2 SILVER CARD
   ═══════════════════════════════════════════════ */
function SilverCard({ b, lang, bp, onVisit, isTR }) {
  const rating = OUR_RATINGS[b.slug] || 0;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-400/30 p-[1px]" style={{ background: 'linear-gradient(135deg, #8a8a8a 0%, #c0c0c0 30%, #8a8a8a 60%, #d4d4d4 100%)' }} data-testid={`bookmaker-card-${b.id}`}>
      <div className="relative rounded-2xl bg-slate-950 p-5">
        <div className="absolute top-0 left-0 w-32 h-32 bg-slate-400/5 rounded-full blur-3xl" />
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-black shrink-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #c0c0c0, #8a8a8a)', color: '#1a1a1a', fontFamily: 'Outfit, sans-serif' }}>2</div>
          <Link to={`/${lang}/${bp}/${b.slug}`} className="shrink-0"><BrandLogo slug={b.slug} name={b.name} size="lg" /></Link>
          <Link to={`/${lang}/${bp}/${b.slug}`} className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.name}</h2>
              <span className="px-2 py-0.5 bg-slate-400/10 border border-slate-400/20 rounded-full text-[9px] font-bold text-slate-400">Top 2</span>
            </div>
            <div className="flex items-center gap-2"><MiniStars rating={rating} /><span className="text-sm font-bold text-amber-400">{rating}</span></div>
          </Link>
          <div className="hidden lg:grid grid-cols-4 gap-1.5">
            {[{ v: `${rating}/5`, l: isTR ? 'Puan' : 'Score' }, { v: `${b.margin_avg || 4}%`, l: isTR ? 'Marj' : 'Margin' }, { v: '45+', l: isTR ? 'Pazar' : 'Markets' }, { v: '5dk', l: isTR ? 'Hız' : 'Speed' }].map(m => (
              <div key={m.l} className="bg-slate-800/60 rounded-lg px-2.5 py-1.5 text-center">
                <p className="text-xs font-bold text-white tabular-nums">{m.v}</p>
                <p className="text-[7px] text-slate-500 uppercase">{m.l}</p>
              </div>
            ))}
          </div>
          <button onClick={(e) => onVisit(b, e)} data-testid={`visit-btn-${b.id}`}
            className="shrink-0 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center gap-2 transition-all hover:scale-105 shadow-lg shadow-emerald-600/20">
            <ExternalLink className="w-4 h-4" />{isTR ? 'Siteye Git' : 'Visit'}
          </button>
        </div>
        <div className="relative z-10 mt-3 flex items-center gap-3">
          <ScoreBar rating={rating} color="#c0c0c0" /><span className="text-[10px] text-slate-500 tabular-nums">{rating}/5</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   #3 BRONZE CARD
   ═══════════════════════════════════════════════ */
function BronzeCard({ b, lang, bp, onVisit, isTR }) {
  const rating = OUR_RATINGS[b.slug] || 0;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-orange-700/30 p-[1px]" style={{ background: 'linear-gradient(135deg, #8B4513 0%, #CD853F 30%, #8B4513 60%, #DEB887 100%)' }} data-testid={`bookmaker-card-${b.id}`}>
      <div className="relative rounded-2xl bg-slate-950 p-5">
        <div className="absolute top-0 left-0 w-32 h-32 bg-orange-500/5 rounded-full blur-3xl" />
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-black shrink-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #CD853F, #8B4513)', color: '#fff', fontFamily: 'Outfit, sans-serif' }}>3</div>
          <Link to={`/${lang}/${bp}/${b.slug}`} className="shrink-0"><BrandLogo slug={b.slug} name={b.name} size="lg" /></Link>
          <Link to={`/${lang}/${bp}/${b.slug}`} className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.name}</h2>
              <span className="px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded-full text-[9px] font-bold text-orange-400">Top 3</span>
            </div>
            <div className="flex items-center gap-2"><MiniStars rating={rating} /><span className="text-sm font-bold text-amber-400">{rating}</span></div>
          </Link>
          <div className="hidden lg:grid grid-cols-4 gap-1.5">
            {[{ v: `${rating}/5`, l: isTR ? 'Puan' : 'Score' }, { v: `${b.margin_avg || 4}%`, l: isTR ? 'Marj' : 'Margin' }, { v: '45+', l: isTR ? 'Pazar' : 'Markets' }, { v: '5dk', l: isTR ? 'Hız' : 'Speed' }].map(m => (
              <div key={m.l} className="bg-slate-800/60 rounded-lg px-2.5 py-1.5 text-center">
                <p className="text-xs font-bold text-white tabular-nums">{m.v}</p>
                <p className="text-[7px] text-slate-500 uppercase">{m.l}</p>
              </div>
            ))}
          </div>
          <button onClick={(e) => onVisit(b, e)} data-testid={`visit-btn-${b.id}`}
            className="shrink-0 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center gap-2 transition-all hover:scale-105 shadow-lg shadow-emerald-600/20">
            <ExternalLink className="w-4 h-4" />{isTR ? 'Siteye Git' : 'Visit'}
          </button>
        </div>
        <div className="relative z-10 mt-3 flex items-center gap-3">
          <ScoreBar rating={rating} color="#CD853F" /><span className="text-[10px] text-slate-500 tabular-nums">{rating}/5</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   #4-5 STANDARD PREMIUM CARD
   ═══════════════════════════════════════════════ */
function StandardCard({ b, rank, lang, bp, onVisit, isTR }) {
  const rating = OUR_RATINGS[b.slug] || 0;
  return (
    <div className="border border-slate-800/60 bg-slate-900/40 hover:border-slate-600 rounded-2xl p-4 transition-all" data-testid={`bookmaker-card-${b.id}`}>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-lg font-black text-slate-400 shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>{rank}</div>
        <Link to={`/${lang}/${bp}/${b.slug}`} className="shrink-0"><BrandLogo slug={b.slug} name={b.name} size="lg" /></Link>
        <Link to={`/${lang}/${bp}/${b.slug}`} className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{b.name}</h2>
          <div className="flex items-center gap-2"><MiniStars rating={rating} /><span className="text-sm font-bold text-amber-400">{rating}</span></div>
        </Link>
        <div className="hidden lg:grid grid-cols-4 gap-1.5">
          {[{ v: `${rating}/5`, l: isTR ? 'Puan' : 'Score' }, { v: `${b.margin_avg || 4}%`, l: isTR ? 'Marj' : 'Margin' }, { v: '45+', l: isTR ? 'Pazar' : 'Markets' }, { v: '5dk', l: isTR ? 'Hız' : 'Speed' }].map(m => (
            <div key={m.l} className="bg-slate-800/60 rounded-lg px-2.5 py-1.5 text-center">
              <p className="text-xs font-bold text-white tabular-nums">{m.v}</p>
              <p className="text-[7px] text-slate-500 uppercase">{m.l}</p>
            </div>
          ))}
        </div>
        <button onClick={(e) => onVisit(b, e)} data-testid={`visit-btn-${b.id}`}
          className="shrink-0 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center gap-2 transition-all hover:scale-105 shadow-lg shadow-emerald-600/20">
          <ExternalLink className="w-4 h-4" />{isTR ? 'Siteye Git' : 'Visit'}
        </button>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <ScoreBar rating={rating} color={BRAND[b.slug]?.textColor || '#22c55e'} /><span className="text-[10px] text-slate-500 tabular-nums">{rating}/5</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */
export default function BookmakersPage() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [bookmakers, setBookmakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const isTR = currentLang === 'tr';

  useEffect(() => {
    fetch(`${API_BASE}/api/bookmakers`)
      .then(r => r.json())
      .then(d => {
        const all = d.bookmakers || [];
        const withLink = all.filter(b => b.affiliate_link?.trim());
        withLink.sort((a, b) => (OUR_RATINGS[b.slug] || 0) - (OUR_RATINGS[a.slug] || 0));
        setBookmakers(withLink);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;

  const bp = ({ en: 'bookmaker', tr: 'bahisci', es: 'casa-apuestas', pt: 'casa-apostas', de: 'buchmacher', fr: 'bookmaker', it: 'bookmaker', nl: 'bookmaker', ru: 'bukmeker', uk: 'bukmeker', ar: 'murahan', id: 'bandar' })[currentLang] || 'bookmaker';

  const handleVisit = (bkm, e) => {
    e.preventDefault(); e.stopPropagation();
    trackBookmakerClick(bkm.id, bkm.name, '', 'bookmakers_page').catch(() => {});
    trackGTMClick(bkm.name, '', '', 'bookmakers_page');
    if (bkm.affiliate_link) window.open(bkm.affiliate_link, '_blank', 'noopener,noreferrer');
  };

  const rest = bookmakers.slice(5);
  const topClicked = [...bookmakers].sort((a, b) => (b.priority || 0) - (a.priority || 0)).slice(0, 5);

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="bookmakers-page">
      <SEOHead title={t('bookmakers.seoTitle', 'Best Bookmakers & Odds Comparison')} description={t('bookmakers.seoDesc', `Compare odds from ${bookmakers.length}+ bookmakers.`)} />

      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight uppercase text-white flex items-center gap-3" style={{ fontFamily: 'Outfit, var(--font-heading), sans-serif' }}>
          <span className="text-3xl md:text-4xl"><Zap className="w-8 h-8 md:w-10 md:h-10 text-emerald-400 fill-emerald-400/30 inline" /></span>{t('bookmakers.title', 'Bookmakers')}
        </h1>
        <p className="text-sm text-slate-400 mt-1">{isTR ? `${bookmakers.length} bahis sitesi analiz edildi ve puanlandı` : `${bookmakers.length} bookmakers analyzed and rated`}</p>
      </div>

      <div className="flex gap-6">
        {/* ── MAIN CONTENT ── */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* #1 Gold */}
          {bookmakers[0] && <GoldCard b={bookmakers[0]} lang={currentLang} bp={bp} onVisit={handleVisit} isTR={isTR} />}
          {/* #2 Silver */}
          {bookmakers[1] && <SilverCard b={bookmakers[1]} lang={currentLang} bp={bp} onVisit={handleVisit} isTR={isTR} />}
          {/* #3 Bronze */}
          {bookmakers[2] && <BronzeCard b={bookmakers[2]} lang={currentLang} bp={bp} onVisit={handleVisit} isTR={isTR} />}
          {/* #4-5 Standard */}
          {bookmakers[3] && <StandardCard b={bookmakers[3]} rank={4} lang={currentLang} bp={bp} onVisit={handleVisit} isTR={isTR} />}
          {bookmakers[4] && <StandardCard b={bookmakers[4]} rank={5} lang={currentLang} bp={bp} onVisit={handleVisit} isTR={isTR} />}

          {/* ── COMPACT LIST 6+ ── */}
          {rest.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {isTR ? 'Diğer Bahis Siteleri' : 'Other Bookmakers'} ({rest.length})
              </h3>
              <div className="space-y-1">
                {rest.map((b, idx) => {
                  const rank = idx + 6;
                  const rating = OUR_RATINGS[b.slug] || b.rating || 0;
                  return (
                    <div key={b.id} className="flex items-center gap-3 border border-slate-800/30 bg-slate-900/20 hover:bg-slate-800/40 hover:border-slate-700/50 rounded-xl px-4 py-2.5 transition-all" data-testid={`bookmaker-card-${b.id}`}>
                      <span className="w-5 text-center text-[11px] font-bold text-slate-600 tabular-nums">{rank}</span>
                      <Link to={`/${currentLang}/${bp}/${b.slug}`} className="shrink-0"><BrandLogo slug={b.slug} name={b.name} size="sm" /></Link>
                      <Link to={`/${currentLang}/${bp}/${b.slug}`} className="flex-1 min-w-0 flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-300 truncate">{b.name}</p>
                      </Link>
                      <div className="hidden md:flex items-center gap-3 text-[10px]">
                        <span className="text-amber-400 font-bold flex items-center gap-0.5"><Star className="w-3 h-3 fill-amber-400" />{rating}</span>
                        <span className="text-slate-600">{b.margin_avg || 4}%</span>
                      </div>
                      <div className="hidden md:flex items-center gap-1.5 w-20 shrink-0">
                        <ScoreBar rating={rating} color={BRAND[b.slug]?.textColor || '#22c55e'} height="h-1" />
                      </div>
                      <button onClick={(e) => handleVisit(b, e)} data-testid={`visit-btn-${b.id}`}
                        className="shrink-0 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-semibold flex items-center gap-1 transition-all">
                        <ExternalLink className="w-3 h-3" />{isTR ? 'Ziyaret' : 'Visit'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── SIDEBAR — Conversion Focused ── */}
        <div className="hidden xl:block w-72 shrink-0">
          <div className="sticky top-20 space-y-4">

            {/* BLOCK 1: TOP 3 ÖNERİLEN */}
            <div className="bg-slate-900/60 backdrop-blur-xl border border-amber-500/15 rounded-2xl p-4 shadow-[0_0_20px_rgba(245,158,11,0.04)]">
              <h4 className="text-[11px] font-black text-amber-400 uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Trophy className="w-4 h-4" />{isTR ? 'Top 3 Önerilen Site' : 'Top 3 Recommended'}
              </h4>
              <div className="space-y-2.5">
                {[
                  { slug: '1xbet', badge: isTR ? 'En Yüksek Bonus' : 'Best Bonus', badgeColor: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
                  { slug: 'bet365', badge: isTR ? 'En Güvenilir' : 'Most Trusted', badgeColor: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
                  { slug: 'betfair', badge: isTR ? 'En Hızlı Ödeme' : 'Fastest Payout', badgeColor: 'text-sky-400 bg-sky-500/10 border-sky-500/20' },
                ].map((item, i) => {
                  const b = bookmakers.find(x => x.slug === item.slug);
                  if (!b) return null;
                  const r = OUR_RATINGS[b.slug] || 0;
                  return (
                    <div key={i} className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-3 hover:border-amber-500/20 transition-all">
                      <div className="flex items-center gap-2.5 mb-2">
                        <BrandLogo slug={b.slug} name={b.name} size="md" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-white truncate">{b.name}</p>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-bold border ${item.badgeColor}`}>{item.badge}</span>
                        </div>
                        <span className="text-sm font-black text-amber-400 tabular-nums">{r}</span>
                      </div>
                      <button onClick={(e) => handleVisit(b, e)}
                        className="w-full py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center gap-1 transition-all hover:scale-[1.02] shadow-md shadow-emerald-600/15">
                        <ExternalLink className="w-3 h-3" />{isTR ? 'Siteye Git' : 'Visit Site'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* BLOCK 2: HIZLI KARŞILAŞTIRMA TABLOSU */}
            <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-2xl p-4">
              <h4 className="text-[11px] font-black text-white uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <BarChart3 className="w-4 h-4 text-amber-400" />{isTR ? 'Hızlı Karşılaştırma' : 'Quick Compare'}
              </h4>
              <div className="overflow-hidden rounded-xl border border-slate-700/30">
                <table className="w-full text-[9px]">
                  <thead>
                    <tr className="bg-slate-800/80">
                      <th className="px-2 py-2 text-left text-slate-500 font-medium"></th>
                      {bookmakers.slice(0, 3).map(b => (
                        <th key={b.id} className="px-1.5 py-2 text-center">
                          <BrandLogo slug={b.slug} name={b.name} size="sm" />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {[
                      { label: 'Bonus', values: ['500$', '100$', '200$'], icon: '🎁' },
                      { label: 'Mobil', values: ['9/10', '10/10', '8/10'], icon: '📱' },
                      { label: isTR ? 'Canlı Bahis' : 'Live', values: ['9/10', '9/10', '10/10'], icon: '⚡' },
                      { label: isTR ? 'Çekim Hızı' : 'Payout', values: ['5dk', '24s', '2s'], icon: '💨' },
                      { label: isTR ? 'Lisans' : 'License', values: ['Curaçao', 'UK GC', 'UK GC'], icon: '🛡️' },
                    ].map((row, ri) => (
                      <tr key={ri} className="hover:bg-slate-800/30 transition-colors">
                        <td className="px-2 py-1.5 text-slate-400 font-medium whitespace-nowrap">
                          <span className="mr-1">{row.icon}</span>{row.label}
                        </td>
                        {row.values.map((v, vi) => (
                          <td key={vi} className="px-1.5 py-1.5 text-center text-white font-semibold">{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-1">
                {bookmakers.slice(0, 3).map(b => (
                  <button key={b.id} onClick={(e) => handleVisit(b, e)}
                    className="py-1 rounded-md bg-emerald-600/80 hover:bg-emerald-500 text-white text-[8px] font-bold transition-all">
                    {isTR ? 'Git' : 'Go'}
                  </button>
                ))}
              </div>
            </div>

            {/* BLOCK 3: BAHİS REHBERİ */}
            <div className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-2xl p-4">
              <h4 className="text-[11px] font-black text-white uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Shield className="w-4 h-4 text-emerald-400" />{isTR ? 'Bahis Rehberi' : 'Betting Guide'}
              </h4>
              <div className="space-y-1.5">
                {[
                  { label: isTR ? 'Bonus Nasıl Alınır?' : 'How to Claim Bonuses?', path: `/${currentLang}/how-to-claim-bonus`, icon: Award },
                  { label: isTR ? 'Güvenilir Site Nasıl Seçilir?' : 'How to Choose a Safe Site?', path: `/${currentLang}/safe-betting-sites`, icon: Shield },
                  { label: isTR ? 'Oran Karşılaştırma Rehberi' : 'Odds Comparison Guide', path: `/${currentLang}/odds-comparison`, icon: BarChart3 },
                  { label: isTR ? 'En İyi Bahis Siteleri 2026' : 'Best Betting Sites 2026', path: `/${currentLang}/best-betting-sites`, icon: Trophy },
                ].map((item, i) => (
                  <Link key={i} to={item.path}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-800/30 border border-slate-700/20 hover:border-emerald-500/20 hover:bg-slate-800/60 transition-all group">
                    <item.icon className="w-3.5 h-3.5 text-slate-600 group-hover:text-emerald-400 transition-colors shrink-0" />
                    <span className="text-[10px] text-slate-400 group-hover:text-slate-200 transition-colors leading-tight">{item.label}</span>
                    <ChevronRight className="w-3 h-3 text-slate-700 group-hover:text-emerald-400 transition-colors ml-auto shrink-0" />
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* ── MOBILE SIDEBAR (below content) ── */}
        <div className="xl:hidden mt-8 space-y-4">
          {/* Mobile Top 3 */}
          <div className="bg-slate-900/60 border border-amber-500/15 rounded-2xl p-4">
            <h4 className="text-xs font-black text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Trophy className="w-4 h-4" />{isTR ? 'Top 3 Önerilen' : 'Top 3 Recommended'}
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { slug: '1xbet', badge: isTR ? 'En İyi Bonus' : 'Best Bonus' },
                { slug: 'bet365', badge: isTR ? 'Güvenilir' : 'Trusted' },
                { slug: 'betfair', badge: isTR ? 'Hızlı Ödeme' : 'Fast Payout' },
              ].map((item, i) => {
                const b = bookmakers.find(x => x.slug === item.slug);
                if (!b) return null;
                return (
                  <div key={i} className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-3 text-center">
                    <div className="flex justify-center mb-1.5"><BrandLogo slug={b.slug} name={b.name} size="md" /></div>
                    <p className="text-[10px] font-bold text-white mb-0.5">{b.name}</p>
                    <p className="text-[8px] text-amber-400 mb-2">{item.badge}</p>
                    <button onClick={(e) => handleVisit(b, e)}
                      className="w-full py-1 rounded-md bg-emerald-600 text-white text-[9px] font-bold">
                      <ExternalLink className="w-2.5 h-2.5 inline mr-0.5" />{isTR ? 'Git' : 'Go'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Mobile Guide Links */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4">
            <h4 className="text-xs font-black text-white uppercase tracking-wider mb-3"><Shield className="w-4 h-4 inline mr-1.5 text-emerald-400" />{isTR ? 'Bahis Rehberi' : 'Guide'}</h4>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { label: isTR ? 'Bonus Rehberi' : 'Bonus Guide', path: `/${currentLang}/how-to-claim-bonus` },
                { label: isTR ? 'Güvenli Site Seçimi' : 'Safe Sites', path: `/${currentLang}/safe-betting-sites` },
                { label: isTR ? 'Oran Karşılaştırma' : 'Odds Compare', path: `/${currentLang}/odds-comparison` },
                { label: isTR ? 'En İyi Siteler 2026' : 'Best Sites 2026', path: `/${currentLang}/best-betting-sites` },
              ].map((item, i) => (
                <Link key={i} to={item.path} className="text-[10px] text-slate-400 hover:text-emerald-400 bg-slate-800/30 border border-slate-700/20 rounded-lg px-2.5 py-2 text-center transition-colors">{item.label}</Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {bookmakers.length === 0 && <div className="text-center text-slate-500 py-16">{isTR ? 'Bahis sitesi bulunamadı' : 'No bookmakers available'}</div>}
    </div>
  );
}
